/**
 * useJarvisRuntime — Runtime de áudio client-side para Jarvis.
 *
 * Responsabilidades:
 *  - Captura microfone com VAD (detecta início/fim de fala)
 *  - Em fim de fala: envia áudio para `jarvis-pipeline` action `process_turn`
 *  - Reproduz áudio de resposta automaticamente via AudioContext compartilhado
 *  - Wake-word filter (modo invocation): só processa se wake-word estiver presente
 *  - Barge-in: se nova fala começar enquanto Jarvis está falando, envia `interrupt` e para playback
 *
 * Uso:
 *   const { start, stop, state, level, lastResponse, ttsWarning } = useJarvisRuntime({
 *     sessionId, mode: "invocation", wakeWords: ["jarvis"]
 *   });
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createVAD, detectWakeWord, type VADController } from "@/lib/jarvis/vad";

export type JarvisRuntimeState =
  | "idle"
  | "starting"
  | "listening"
  | "thinking"
  | "speaking"
  | "error";

export interface UseJarvisRuntimeOptions {
  sessionId: string;
  /** "passive" (não responde), "invocation" (só com wake-word), "proactive" (sempre responde) */
  mode?: "passive" | "invocation" | "proactive";
  wakeWords?: string[];
  voice?: string;
  taskClass?: "fast" | "balanced" | "reasoning";
  /** Pula TTS, retorna apenas texto */
  skipTTS?: boolean;
  /** Persona built-in: "syra" (Sarah) ou "jarvis" (George) */
  personaKey?: "syra" | "jarvis";
  /** Hint de idioma para STT (default "pt") */
  language?: string;
  /** Callback quando uma resposta completa é recebida */
  onResponse?: (resp: { text: string; turnId: string; e2eMs: number }) => void;
  /** Callback de erro */
  onError?: (err: string) => void;
}

export interface JarvisLatencyBreakdown {
  stt_ms: number;
  llm_ms: number;
  tts_ms: number;
  total_ms: number;
}

export interface JarvisResponse {
  turnId: string;
  text: string;
  e2eMs: number;
  provider: string;
  interrupted: boolean;
  latency?: JarvisLatencyBreakdown;
}

export interface TTSWarning {
  status: string;
  reason?: string;
  at: number;
}

// Map MIME → format string esperado pelo navegador para decodeAudioData
const FORMAT_MIME: Record<string, string> = {
  mp3: "audio/mpeg",
  mpeg: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  opus: "audio/ogg; codecs=opus",
  aac: "audio/aac",
  m4a: "audio/mp4",
  webm: "audio/webm",
};

function resolveMime(format: string | undefined): string {
  if (!format) return "audio/mpeg";
  const k = format.toLowerCase();
  return FORMAT_MIME[k] ?? `audio/${k}`;
}

// Decode base64 → ArrayBuffer (sem usar Blob/URL para evitar leaks)
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const len = bin.length;
  const buf = new Uint8Array(len);
  for (let i = 0; i < len; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

export function useJarvisRuntime(opts: UseJarvisRuntimeOptions) {
  const {
    sessionId,
    mode = "invocation",
    wakeWords = ["jarvis"],
    voice,
    taskClass = "balanced",
    skipTTS = false,
    personaKey,
    language = "pt",
    onResponse,
    onError,
  } = opts;

  const [state, setState] = useState<JarvisRuntimeState>("idle");
  const [level, setLevel] = useState(0);
  const [lastResponse, setLastResponse] = useState<JarvisResponse | null>(null);
  const [ttsWarning, setTtsWarning] = useState<TTSWarning | null>(null);

  const vadRef = useRef<VADController | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const inFlightRef = useRef<AbortController | null>(null);
  const stateRef = useRef<JarvisRuntimeState>("idle");
  const sessionIdRef = useRef<string>(sessionId);
  const mountedRef = useRef(true);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Cria/retoma AudioContext (precisa estar dentro de user gesture na 1ª vez)
  const ensureAudioContext = useCallback(async (): Promise<AudioContext | null> => {
    try {
      if (!audioCtxRef.current) {
        const Ctor: typeof AudioContext =
          (window.AudioContext as typeof AudioContext) ||
          ((window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext as typeof AudioContext);
        if (!Ctor) {
          console.warn("[useJarvisRuntime] AudioContext not supported in this browser");
          return null;
        }
        audioCtxRef.current = new Ctor();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") await ctx.resume();
      return ctx;
    } catch (e) {
      console.warn("[useJarvisRuntime] ensureAudioContext failed", e);
      return null;
    }
  }, []);

  const stopCurrentSource = useCallback(() => {
    const src = currentSourceRef.current;
    if (src) {
      try { src.onended = null; src.stop(); } catch { /* noop */ }
      try { src.disconnect(); } catch { /* noop */ }
      currentSourceRef.current = null;
    }
  }, []);

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = r.result as string;
        resolve(result.split(",")[1] ?? "");
      };
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });

  const sendInterrupt = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    try {
      await supabase.functions.invoke("jarvis-pipeline", {
        body: { action: "interrupt", session_id: sid },
      });
    } catch (e) {
      console.warn("[useJarvisRuntime] interrupt failed", e);
    }
  }, []);

  const handleSpeechStart = useCallback(() => {
    // Barge-in: se Jarvis estava falando, interrompe
    if (stateRef.current === "speaking" || stateRef.current === "thinking") {
      stopCurrentSource();
      inFlightRef.current?.abort("barge_in");
      void sendInterrupt();
    }
    setState("listening");
  }, [sendInterrupt, stopCurrentSource]);

  const playBuffer = useCallback(async (audioBase64: string, format: string | undefined): Promise<boolean> => {
    const ctx = await ensureAudioContext();
    if (!ctx) return false;
    try {
      const arrBuf = base64ToArrayBuffer(audioBase64);
      // decodeAudioData consome o ArrayBuffer; clone se precisar reutilizar
      const audioBuffer = await ctx.decodeAudioData(arrBuf.slice(0));
      stopCurrentSource();
      const src = ctx.createBufferSource();
      src.buffer = audioBuffer;
      src.connect(ctx.destination);
      currentSourceRef.current = src;
      return await new Promise<boolean>((resolve) => {
        src.onended = () => {
          if (currentSourceRef.current === src) currentSourceRef.current = null;
          if (mountedRef.current && stateRef.current === "speaking") setState("listening");
          resolve(true);
        };
        try {
          src.start(0);
          if (mountedRef.current) setState("speaking");
        } catch (e) {
          console.warn("[useJarvisRuntime] source.start failed", e);
          resolve(false);
        }
      });
    } catch (e) {
      console.warn("[useJarvisRuntime] decodeAudioData failed, format=", format, e);
      return false;
    }
  }, [ensureAudioContext, stopCurrentSource]);

  const handleSpeechEnd = useCallback(async (audioBlob: Blob, durationMs: number) => {
    const activeSessionId = sessionIdRef.current;
    if (!activeSessionId) {
      console.warn("[useJarvisRuntime] no session_id yet, dropping segment");
      setState("listening");
      return;
    }
    if (audioBlob.size < 2000) {
      console.log("[useJarvisRuntime] segment too short, skipping", { size: audioBlob.size, durationMs });
      setState("listening");
      return;
    }
    setState("thinking");
    inFlightRef.current?.abort("superseded");
    const ctrl = new AbortController();
    inFlightRef.current = ctrl;

    try {
      const audioBase64 = await blobToBase64(audioBlob);
      if (!audioBase64 || audioBase64.length < 100) {
        console.warn("[useJarvisRuntime] empty audio base64, skipping", { len: audioBase64?.length ?? 0 });
        setState("listening");
        return;
      }
      const mimetype = audioBlob.type || "audio/webm";

      const { data, error } = await supabase.functions.invoke("jarvis-pipeline", {
        body: {
          action: "process_turn",
          session_id: activeSessionId,
          audio_base64: audioBase64,
          audio_mimetype: mimetype,
          task_class: taskClass,
          voice,
          language,
          skip_tts: skipTTS,
          ...(personaKey ? { persona_key: personaKey } : {}),
        },
      });

      if (ctrl.signal.aborted) return;
      const softErrors = new Set(["stt_failed", "missing_input", "no_provider"]);
      if (data && data.success === false && softErrors.has(data.error)) {
        console.warn("[useJarvisRuntime] soft STT error, skipping segment", data.error, data.detail);
        setState("listening");
        return;
      }
      if (error) throw new Error(error.message || "edge_invoke_failed");
      if (!data?.success) {
        const detail = data?.detail || data?.error || "pipeline_failed";
        throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
      }

      const transcriptText: string = data.transcript?.text ?? "";

      // Filtro wake-word para modo "invocation"
      if (mode === "invocation" && wakeWords.length > 0) {
        const matched = detectWakeWord(transcriptText, wakeWords);
        if (!matched) {
          console.log("[useJarvisRuntime] no wake-word, ignoring", { transcriptText });
          setState("listening");
          return;
        }
      }
      if (mode === "passive") {
        setState("listening");
        return;
      }

      // Sinaliza falhas de TTS de forma persistente para a UI
      const ttsStatus: string | undefined = data.tts_status;
      const ttsReason: string | undefined = data.tts_reason;
      if (ttsStatus && ttsStatus !== "ok") {
        setTtsWarning({ status: ttsStatus, reason: ttsReason, at: Date.now() });
      } else if (ttsStatus === "ok" && ttsWarning) {
        setTtsWarning(null);
      }

      const response: JarvisResponse = {
        turnId: data.turn_id,
        text: data.response.text,
        e2eMs: data.e2e_ms,
        provider: data.response.provider,
        interrupted: !!data.interrupted,
        latency: data.latency
          ? {
              stt_ms: Number(data.latency.stt_ms ?? 0),
              llm_ms: Number(data.latency.llm_ms ?? 0),
              tts_ms: Number(data.latency.tts_ms ?? 0),
              total_ms: Number(data.latency.total_ms ?? data.e2e_ms ?? 0),
            }
          : undefined,
      };

      // Playback via AudioContext (sem ObjectURL → zero leaks)
      if (data.audio?.base64) {
        const ok = await playBuffer(data.audio.base64, data.audio.format);
        if (!ok) setState("listening");
      } else {
        setState("listening");
      }

      setLastResponse(response);
      onResponse?.({ text: response.text, turnId: response.turnId, e2eMs: response.e2eMs });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[useJarvisRuntime] turn error", msg);
      onError?.(msg);
      setState("listening");
    } finally {
      if (inFlightRef.current === ctrl) inFlightRef.current = null;
    }
  }, [mode, wakeWords, voice, taskClass, skipTTS, personaKey, language, onResponse, onError, playBuffer, ttsWarning]);

  const start = useCallback(async (overrideSessionId?: string) => {
    if (vadRef.current?.isActive()) return;
    if (overrideSessionId) sessionIdRef.current = overrideSessionId;
    setState("starting");
    try {
      // Inicializa AudioContext dentro do user gesture (start é chamado por click)
      await ensureAudioContext();
      const vad = createVAD({
        onSpeechStart: handleSpeechStart,
        onSpeechEnd: handleSpeechEnd,
        onLevel: setLevel,
      });
      await vad.start();
      vadRef.current = vad;
      setState("listening");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useJarvisRuntime] start failed", msg);
      onError?.(msg);
      setState("error");
    }
  }, [handleSpeechStart, handleSpeechEnd, onError, ensureAudioContext]);

  const stop = useCallback(() => {
    vadRef.current?.stop();
    vadRef.current = null;
    stopCurrentSource();
    inFlightRef.current?.abort("stopped");
    inFlightRef.current = null;
    setState("idle");
    setLevel(0);
  }, [stopCurrentSource]);

  // Cleanup ao desmontar — fecha AudioContext para liberar recursos
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      vadRef.current?.stop();
      vadRef.current = null;
      stopCurrentSource();
      inFlightRef.current?.abort("unmount");
      inFlightRef.current = null;
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== "closed") {
        ctx.close().catch(() => { /* noop */ });
      }
      audioCtxRef.current = null;
    };
  }, [stopCurrentSource]);

  return { start, stop, state, level, lastResponse, ttsWarning };
}
